import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoad } from "../../shared/loading/useLoad";
import { ChangeCountsLine, ChangePageControls } from "./ChangePageControls";
import { getSegmentPage, getTextDiff, type SegmentChange, type TextSnapshot } from "./segmentChangesApi";
import { changeToneClass } from "../../ui/styles/changeStyles";
import LoadStatus from "../../ui/feedback/LoadStatus";

export interface ComparisonTarget { id: string; itemId: string; head: string }

export default function SegmentChangeList({ target }: { target: ComparisonTarget }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const load = useCallback((signal: AbortSignal) => getSegmentPage(target.id, target.itemId, target.head, offset, signal), [target.id, target.itemId, target.head, offset]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const page = state.value;
  return <div className="space-y-2 py-2">
    <ChangeCountsLine counts={page.counts} />
    <SegmentRow key="document" target={target} segment={page.documentText} />
    {page.segments.map((segment) => <SegmentRow key={segment.path} target={target} segment={segment} />)}
    <ChangePageControls offset={page.offset} limit={page.limit} nextOffset={page.nextOffset} total={page.counts.total} onPage={setOffset} />
  </div>;
}

function SegmentRow({ target, segment }: { target: ComparisonTarget; segment: SegmentChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return <details className="border-t p-2" onToggle={(event) => { setOpen(event.currentTarget.open); }}>
    <summary className="cursor-pointer text-xs"><span className="font-mono">{segment.path || t("importProposals.page.documentText")}</span> · {t(`importProposals.changes.${segment.change}`)}</summary>
    {open ? <div className="space-y-2 pt-2">
      <Snapshot label={t("importProposals.page.before")} value={segment.old} />
      <Snapshot label={t("importProposals.page.after")} value={segment.new} />
      {segment.change === "unchanged" ? null : <SegmentTextDiff target={target} path={segment.path} />}
    </div> : null}
  </details>;
}

function Snapshot({ label, value }: { label: string; value: TextSnapshot | null }) {
  if (value === null) return null;
  return <div className="break-all text-xs"><span>{label}: </span><code>{value.reference} [{value.start}, {value.end})</code><br /><code>{value.sha256}</code></div>;
}

function SegmentTextDiff({ target, path }: { target: ComparisonTarget; path: string }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const load = useCallback((signal: AbortSignal) => getTextDiff(target.id, target.itemId, target.head, path, offset, signal), [target.id, target.itemId, target.head, path, offset]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const diff = state.value;
  return <div className="space-y-2">
    <p className="app-text-muted text-xs">{t("importProposals.page.diffContext", { prefix: diff.prefix, suffix: diff.suffix })}</p>
    <div className="grid gap-2 sm:grid-cols-2">
      <div><p className="text-xs">{t("importProposals.page.removedText", { count: diff.removedCharacters })}</p><pre className={`${changeToneClass("removed")} min-h-8 whitespace-pre-wrap break-words rounded p-2 text-xs`}>{diff.removed}</pre></div>
      <div><p className="text-xs">{t("importProposals.page.addedText", { count: diff.addedCharacters })}</p><pre className={`${changeToneClass("added")} min-h-8 whitespace-pre-wrap break-words rounded p-2 text-xs`}>{diff.added}</pre></div>
    </div>
    <ChangePageControls offset={diff.offset} limit={diff.limit} nextOffset={diff.nextOffset} total={Math.max(diff.removedCharacters, diff.addedCharacters)} onPage={setOffset} />
  </div>;
}
